import { useState, useCallback } from 'react';
import { useScrollReveal } from '@/hooks/useScrollReveal';

export default function TLROI() {
  const { ref: labelRef, revealed: labelRevealed } = useScrollReveal();
  const { ref: titleRef, revealed: titleRevealed } = useScrollReveal({ delay: 100 });
  const { ref: descRef, revealed: descRevealed } = useScrollReveal({ delay: 200 });
  const { ref: calcRef, revealed: calcRevealed } = useScrollReveal({ delay: 300 });

  const [avgCheck, setAvgCheck] = useState(100000);
  const [conversion, setConversion] = useState(1);

  const deals = Math.round((100 * conversion) / 100);
  const revenue = deals * avgCheck;
  const cost = 14900;
  const roi = (revenue / cost).toFixed(1);

  const formatNum = useCallback((n: number) => n.toLocaleString('ru-RU'), []);

  return (
    <section className="bg-background-50 py-20 md:py-28">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <span
            ref={labelRef}
            className={`inline-block text-[12px] font-semibold uppercase tracking-[0.18em] text-primary-600 reveal-fade-up ${labelRevealed ? 'revealed' : ''}`}
          >
            Окупаемость
          </span>
          <h2
            ref={titleRef}
            className={`mt-4 font-display font-extrabold text-[34px] md:text-[46px] leading-[1.1] tracking-tightest text-foreground-950 reveal-fade-up ${titleRevealed ? 'revealed' : ''}`}
          >
            Сколько вам принесёт AIGAM
          </h2>
          <p
            ref={descRef}
            className={`mt-5 text-[16px] text-foreground-500 leading-relaxed max-w-md reveal-fade-up ${descRevealed ? 'revealed' : ''}`}
          >
            Подвиньте ползунки под свою экономику. Расчёт сделан для тарифа START (14 900 ₽ и до 100 лидов в месяц).
          </p>
          <div className="mt-8 flex items-center gap-2 text-[13px] text-foreground-500">
            <i className="ri-information-line text-primary-500"></i>
            Это модельный расчёт, а не обещание результата.
          </div>
        </div>

        <div className="lg:col-span-7">
          <div
            ref={calcRef}
            className={`rounded-3xl border border-background-200 bg-background-100 p-6 md:p-10 reveal-fade-up ${calcRevealed ? 'revealed' : ''}`}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
              <div>
                <label className="text-[13px] font-semibold uppercase tracking-wider text-foreground-500">
                  Средний чек сделки
                </label>
                <div className="mt-3 font-display font-extrabold text-[36px] md:text-[42px] tracking-tightest text-foreground-950">
                  {formatNum(avgCheck)} <span className="text-primary-500">₽</span>
                </div>
                <input
                  type="range"
                  min={10000}
                  max={1000000}
                  step={10000}
                  value={avgCheck}
                  onChange={(e) => setAvgCheck(Number(e.target.value))}
                  className="mt-4 w-full accent-primary-500 cursor-pointer"
                />
                <div className="mt-2 flex justify-between text-[11px] text-foreground-400 tabular-nums">
                  <span>10 000 ₽</span>
                  <span>1 000 000 ₽</span>
                </div>
              </div>
              <div>
                <label className="text-[13px] font-semibold uppercase tracking-wider text-foreground-500">
                  Конверсия в сделку
                </label>
                <div className="mt-3 font-display font-extrabold text-[36px] md:text-[42px] tracking-tightest text-foreground-950">
                  {conversion}<span className="text-primary-500">%</span>
                </div>
                <input
                  type="range"
                  min={1}
                  max={20}
                  step={1}
                  value={conversion}
                  onChange={(e) => setConversion(Number(e.target.value))}
                  className="mt-4 w-full accent-primary-500 cursor-pointer"
                />
                <div className="mt-2 flex justify-between text-[11px] text-foreground-400 tabular-nums">
                  <span>1%</span>
                  <span>20%</span>
                </div>
              </div>
            </div>

            <div className="mt-8 pt-8 border-t border-background-200 grid grid-cols-3 gap-3 md:gap-5">
              <div className="bg-background-50 rounded-xl p-4 md:p-5 border border-background-200">
                <div className="text-[11px] md:text-[12px] uppercase tracking-wider text-foreground-400 font-semibold">Сделок / мес</div>
                <div className="mt-2 font-display font-extrabold text-[22px] md:text-[28px] text-foreground-950 tabular-nums leading-none">{deals}</div>
              </div>
              <div className="bg-background-50 rounded-xl p-4 md:p-5 border border-background-200">
                <div className="text-[11px] md:text-[12px] uppercase tracking-wider text-foreground-400 font-semibold">Выручка / мес</div>
                <div className="mt-2 font-display font-extrabold text-[22px] md:text-[28px] text-foreground-950 tabular-nums leading-none">
                  {formatNum(revenue)} ₽
                </div>
              </div>
              <div className="bg-background-950 rounded-xl p-4 md:p-5 text-background-50">
                <div className="text-[11px] md:text-[12px] uppercase tracking-wider text-primary-400 font-semibold">Окупаемость</div>
                <div className="mt-2 font-display font-extrabold text-[22px] md:text-[28px] tabular-nums leading-none">{roi}×</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}